import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import DuplicateProjectModal from "../components/DuplicateProjectModal";
import { API_BASE } from "../config/orbisConfig";



const defaultFilters = {
    search: "",
    stage: "",
    status: "",
    health_status: "",
    project_type: "",
    priority: "",
};

const defaultViewMode = "all";

const stageOptions = [
    { value: "", label: "Todas las etapas" },
    { value: "inicio", label: "inicio" },
    { value: "planeacion", label: "planeacion" },
    { value: "ejecucion", label: "ejecucion" },
    { value: "monitoreo", label: "monitoreo" },
    { value: "cierre", label: "cierre" },
    { value: "cancelado", label: "cancelado" },
];

const statusOptions = [
    { value: "", label: "Todos los estatus" },
    { value: "activo", label: "activo" },
    { value: "pausado", label: "pausado" },
    { value: "terminado", label: "terminado" },
    { value: "cancelado", label: "cancelado" },
];

const healthOptions = [
    { value: "", label: "Toda la salud" },
    { value: "verde", label: "verde" },
    { value: "amarillo", label: "amarillo" },
    { value: "rojo", label: "rojo" },
];

const priorityOptions = [
    { value: "", label: "Todas las prioridades" },
    { value: "baja", label: "baja" },
    { value: "media", label: "media" },
    { value: "alta", label: "alta" },
    { value: "critica", label: "critica" },
];

const viewModeOptions = [
    {
        value: "all",
        label: "Todos",
        helper: "Vista ejecutiva completa del portafolio autorizado.",
    },
    {
        value: "as_pm",
        label: "Como PM",
        helper: "Proyectos que gobiernas directamente como Project Manager.",
    },
    {
        value: "as_sponsor",
        label: "Sponsor",
        helper: "Proyectos donde das respaldo, decisión o patrocinio institucional.",
    },
    {
        value: "as_responsible",
        label: "Responsable",
        helper: "Proyectos donde tienes tareas asignadas para ejecutar y documentar avance.",
    },
    {
        value: "as_reviewer",
        label: "Revisor",
        helper: "Proyectos donde debes validar avances, evidencia o solicitudes de cambio.",
    },
    {
        value: "participating",
        label: "Donde participo",
        helper: "Proyectos donde tienes cualquier rol: PM, sponsor, responsable, revisor o propietario de riesgos/entregables.",
    },
];

function getLoggedUserSignals() {
    return {
        displayName:
            localStorage.getItem("displayName") ||
            localStorage.getItem("user_name") ||
            localStorage.getItem("userDisplayName") ||
            "",
        email:
            localStorage.getItem("email") ||
            localStorage.getItem("user_email") ||
            localStorage.getItem("userEmail") ||
            "",
        userId:
            localStorage.getItem("user_id") ||
            localStorage.getItem("uid") ||
            "",
        avatarUrl:
            localStorage.getItem("avatar_url") ||
            localStorage.getItem("user_avatar") ||
            localStorage.getItem("profile_photo") ||
            localStorage.getItem("photo_url") ||
            "",
    };
}

function getInitials(name = "", email = "") {
    const source = String(name || email || "Usuario PM").trim();
    const parts = source.split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
        return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    }

    return source.slice(0, 2).toUpperCase();
}

function UserAvatar({ user }) {
    const initials = getInitials(user?.displayName, user?.email);

    if (user?.avatarUrl) {
        return (
            <img
                src={user.avatarUrl}
                alt={user?.displayName || "Usuario"}
                style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #e0e7ff",
                    boxShadow: "0 8px 20px rgba(15,23,42,.12)",
                }}
            />
        );
    }

    return (
        <div
            style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(135deg, #0f172a, #2563eb)",
                color: "#ffffff",
                fontWeight: 900,
                letterSpacing: ".04em",
                border: "2px solid #e0e7ff",
                boxShadow: "0 8px 20px rgba(15,23,42,.12)",
            }}
        >
            {initials}
        </div>
    );
}

function ProjectMiniLabel({ label, tooltip }) {
    return (
        <div
            style={{
                color: "#7dd3fc",
                fontSize: 10,
                fontWeight: 950,
                textTransform: "uppercase",
                letterSpacing: ".12em",
                marginBottom: 6,
            }}
        >
            {label}
            <OrbisTooltip text={tooltip} />
        </div>
    );
}

function OrbisTooltip({ text }) {
    if (!text) return null;

    return (
        <span
            title={text}
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 16,
                marginLeft: 6,
                borderRadius: "50%",
                border: "1px solid rgba(125,211,252,.45)",
                color: "#7dd3fc",
                fontSize: 10,
                fontWeight: 950,
                cursor: "help",
                verticalAlign: "middle",
            }}
        >
            ?
        </span>
    );
}

function Badge({ children, bg = "rgba(14,165,233,.14)", color = "#bfdbfe", border = "1px solid rgba(125,211,252,.24)", tooltip }) {
    return (
        <span
            title={tooltip || undefined}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 800,
                background: bg,
                color,
                border,
                marginRight: 6,
                marginBottom: 6,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

function HealthBadge({ health }) {
    const styles = {
        verde: {
            background: "rgba(34,197,94,.14)",
            color: "#bbf7d0",
            border: "1px solid rgba(34,197,94,.35)",
            dot: "#22c55e",
            label: "En control",
        },
        amarillo: {
            background: "rgba(250,204,21,.14)",
            color: "#fde68a",
            border: "1px solid rgba(250,204,21,.35)",
            dot: "#f59e0b",
            label: "En atención",
        },
        rojo: {
            background: "rgba(248,113,113,.16)",
            color: "#fecaca",
            border: "1px solid rgba(248,113,113,.35)",
            dot: "#ef4444",
            label: "Crítico",
        },
    };

    const config = styles[health] || {
        background: "#f3f4f6",
        color: "#dbeafe",
        border: "1px solid #e5e7eb",
        dot: "#9ca3af",
        label: "Sin definir",
    };

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 850,
                background: config.background,
                color: config.color,
                border: config.border,
                marginRight: 6,
                marginBottom: 6,
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: config.dot,
                    display: "inline-block",
                }}
            />
            {config.label}
        </span>
    );
}

function getPriorityScoreLabel(score) {
    const n = Number(score || 0);

    if (n >= 70) {
        return { label: "Muy alta", bg: "#fef2f2", color: "#991b1b", border: "#fecaca" };
    }
    if (n >= 40) {
        return { label: "Alta", bg: "#fffbeb", color: "#92400e", border: "#fde68a" };
    }
    if (n >= 20) {
        return { label: "Media", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
    }

    return { label: "Baja", bg: "#ecfdf5", color: "#166534", border: "#bbf7d0" };
}

function PriorityScoreBadge({ score }) {
    const config = getPriorityScoreLabel(score);

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                background: config.bg,
                color: config.color,
                border: `1px solid ${config.border}`,
                marginRight: 6,
                marginBottom: 6,
            }}
        >
            Score {Number(score || 0).toFixed(0)} · {config.label}
        </span>
    );
}

function ActionButton({
    children,
    onClick,
    bg = "#ffffff",
    color = "#0f172a",
    border = "1px solid rgba(255,255,255,.5)",
    disabled = false,
    type = "button",
}) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            style={{
                padding: "10px 15px",
                borderRadius: 999,
                border,
                background: disabled ? "#9ca3af" : bg,
                color,
                fontWeight: 850,
                cursor: disabled ? "not-allowed" : "pointer",
                marginRight: 8,
                marginBottom: 8,
                boxShadow: disabled ? "none" : "0 12px 26px rgba(0,0,0,.18)", transition: "all .18s ease",
            }}
        >
            {children}
        </button>
    );
}

function SectionCard({ title, right, children }) {
    return (
        <div
            style={{
                background: "rgba(2,25,48,.88)",
                borderRadius: 22,
                padding: 22,
                boxShadow: "0 20px 44px rgba(0,0,0,.22)",
                border: "1px solid rgba(0,178,255,.22)",
                marginBottom: 18,
                backdropFilter: "blur(10px)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                    flexWrap: "wrap",
                }}
            >
                <h3 style={{ margin: 0, fontSize: 18, color: "#ffffff", letterSpacing: "-.01em" }}>{title}</h3>
                <div>{right}</div>
            </div>
            {children}
        </div>
    );
}

function StatCard({ title, value, subtitle, bg = "#ffffff", onClick, active = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={onClick ? "Filtrar portafolio por este indicador" : undefined}
            style={{
                textAlign: "left",
                width: "100%",
                background: active
                    ? "linear-gradient(180deg, rgba(14,165,233,.22), rgba(2,132,199,.12))"
                    : "linear-gradient(180deg, rgba(8,47,73,.92), rgba(2,25,48,.92))",
                border: active ? "1px solid rgba(125,211,252,.9)" : "1px solid rgba(0,178,255,.28)",
                borderRadius: 18,
                padding: 18,
                boxShadow: active ? "0 0 0 3px rgba(14,165,233,.12)" : "0 14px 32px rgba(0,0,0,.18)",
                minHeight: 118,
                cursor: onClick ? "pointer" : "default",
            }}
        >
            <div style={{ fontSize: 11, color: "#7dd3fc", marginBottom: 8, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".14em" }}>{title}</div>
            <div style={{ fontSize: 30, fontWeight: 950, color: "#ffffff", marginBottom: 6, letterSpacing: "-.03em" }}>
                {value}
            </div>
            <div style={{ fontSize: 12, color: "#bfdbfe", fontWeight: 750, lineHeight: 1.4 }}>{subtitle}</div>
        </button>
    );
}

function Input({ label, value, onChange, ...props }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#7dd3fc", marginBottom: 6, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
            <input
                value={value ?? ""}
                onChange={onChange}
                {...props}
                style={{
                    width: "100%",
                    padding: "11px 13px",
                    borderRadius: 14,
                    border: "1px solid rgba(125,211,252,.45)",
                    outline: "none",
                    fontSize: 13,
                    color: "#e0f2fe",
                    boxSizing: "border-box",
                    background: "rgba(2,25,48,.72)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,.04)",
                }}
            />
        </div>
    );
}

function Select({ label, value, onChange, children }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#7dd3fc", marginBottom: 6, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
            <select
                value={value ?? ""}
                onChange={onChange}
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(125,211,252,.45)",
                    outline: "none",
                    fontSize: 14,
                    color: "#e0f2fe",
                    boxSizing: "border-box",
                    background: "rgba(2,25,48,.72)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,.04)",
                }}
            >
                {children}
            </select>
        </div>
    );
}


function DualHorizontalScroll({ minWidth = 1350, height = 12, children }) {
    const topRef = React.useRef(null);
    const bottomRef = React.useRef(null);
    const innerRef = React.useRef(null);
    const syncingRef = React.useRef(false);
    const [trackWidth, setTrackWidth] = React.useState(minWidth);

    React.useEffect(() => {
        const updateWidth = () => {
            if (innerRef.current) {
                setTrackWidth(Math.max(minWidth, innerRef.current.scrollWidth || minWidth));
            }
        };

        updateWidth();

        let observer = null;
        if (window.ResizeObserver && innerRef.current) {
            observer = new ResizeObserver(updateWidth);
            observer.observe(innerRef.current);
        }

        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            if (observer) observer.disconnect();
        };
    }, [minWidth, children]);

    const syncScroll = (source, targetA) => {
        if (!source) return;
        if (syncingRef.current) return;
        syncingRef.current = true;
        const left = source.scrollLeft;
        if (targetA) targetA.scrollLeft = left;
        requestAnimationFrame(() => {
            syncingRef.current = false;
        });
    };

    return (
        <div>
            <div
                ref={topRef}
                onScroll={() => syncScroll(topRef.current, bottomRef.current)}
                style={{ overflowX: "auto", overflowY: "hidden", height, marginBottom: 6 }}
            >
                <div style={{ width: trackWidth, height: 1 }} />
            </div>

            <div
                ref={bottomRef}
                onScroll={() => syncScroll(bottomRef.current, topRef.current)}
                style={{ overflowX: "auto", overflowY: "visible" }}
            >
                <div ref={innerRef} style={{ minWidth }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

function TableWrapper({ children }) {
    return (
        <div style={{ overflowX: "auto" }}>
            <table
                style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: "0 0",
                    minWidth: 1350,
                }}
            >
                {children}
            </table>
        </div>
    );
}

function Th({ children }) {
    return (
        <th
            style={{
                textAlign: "left",
                padding: "14px 16px",
                background: "rgba(2,25,48,.98)",
                borderBottom: "1px solid rgba(0,178,255,.24)",
                color: "#93c5fd",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                fontWeight: 900,
            }}
        >
            {children}
        </th>
    );
}

function Td({ children }) {
    return (
        <td
            style={{
                padding: "18px 16px",
                borderBottom: "10px solid rgba(0,24,39,.84)",
                verticalAlign: "top",
                fontSize: 13,
                color: "#e0f2fe",
            }}
        >
            {children}
        </td>
    );
}

function ProgressBar({ value }) {
    const safe = Math.max(0, Math.min(100, Number(value || 0)));
    return (
        <div>
            <div style={{ fontWeight: 900, marginBottom: 6, color: "#ffffff" }}>{safe.toFixed(2)}%</div>
            <div
                style={{
                    width: "100%",
                    height: 10,
                    background: "rgba(148,163,184,.25)",
                    borderRadius: 999,
                    overflow: "hidden",
                    minWidth: 120,
                }}
            >
                <div
                    style={{
                        width: `${safe}%`,
                        height: "100%",
                        background: "#2563eb",
                    }}
                />
            </div>
        </div>
    );
}

function getStageBg(stage) {
    const map = {
        inicio: "#e0f2fe",
        planeacion: "#ede9fe",
        ejecucion: "#dcfce7",
        monitoreo: "#fef3c7",
        cierre: "#dbeafe",
        cancelado: "#fee2e2",
    };
    return map[stage] || "#eef2ff";
}

function getStatusBg(status) {
    const map = {
        activo: "#d1fae5",
        pausado: "#fef3c7",
        terminado: "#dbeafe",
        cancelado: "#fee2e2",
    };
    return map[status] || "#eef2ff";
}

function getStageStyle(stage) {
    const styles = {
        inicio: { bg: "rgba(56,189,248,.16)", color: "#bae6fd", border: "1px solid rgba(56,189,248,.35)" },
        planeacion: { bg: "rgba(168,85,247,.16)", color: "#e9d5ff", border: "1px solid rgba(168,85,247,.35)" },
        ejecucion: { bg: "rgba(34,197,94,.16)", color: "#bbf7d0", border: "1px solid rgba(34,197,94,.35)" },
        monitoreo: { bg: "rgba(250,204,21,.16)", color: "#fde68a", border: "1px solid rgba(250,204,21,.35)" },
        cierre: { bg: "rgba(96,165,250,.16)", color: "#bfdbfe", border: "1px solid rgba(96,165,250,.35)" },
        cancelado: { bg: "rgba(248,113,113,.16)", color: "#fecaca", border: "1px solid rgba(248,113,113,.35)" },
    };
    return styles[stage] || { bg: "rgba(148,163,184,.14)", color: "#cbd5e1", border: "1px solid rgba(148,163,184,.28)" };
}

function getStatusStyle(status) {
    const styles = {
        activo: { bg: "rgba(34,197,94,.16)", color: "#bbf7d0", border: "1px solid rgba(34,197,94,.35)" },
        pausado: { bg: "rgba(250,204,21,.16)", color: "#fde68a", border: "1px solid rgba(250,204,21,.35)" },
        terminado: { bg: "rgba(96,165,250,.16)", color: "#bfdbfe", border: "1px solid rgba(96,165,250,.35)" },
        cancelado: { bg: "rgba(248,113,113,.16)", color: "#fecaca", border: "1px solid rgba(248,113,113,.35)" },
    };
    return styles[status] || { bg: "rgba(148,163,184,.14)", color: "#cbd5e1", border: "1px solid rgba(148,163,184,.28)" };
}

function formatDateLong(dateStr) {
    if (!dateStr) return "—";

    try {
        return new Date(dateStr).toLocaleDateString("es-MX", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return dateStr;
    }
}

function formatMoney(value, currency = "MXN") {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || n <= 0) return "—";

    return n.toLocaleString("es-MX", {
        style: "currency",
        currency: currency || "MXN",
        maximumFractionDigits: 0,
    });
}

function getProjectValue(row) {
    return Number(row?.contract_value || row?.budget_planned || row?.budget_actual || 0);
}

function buildPmoPressureText(row) {
    const pieces = [];

    if (Number(row?.overdue_tasks || 0) > 0) {
        pieces.push(`${Number(row.overdue_tasks)} vencida(s)`);
    }
    if (Number(row?.blocked_tasks || 0) > 0) {
        pieces.push(`${Number(row.blocked_tasks)} bloqueada(s)`);
    }
    if (Number(row?.tasks_in_review || 0) > 0) {
        pieces.push(`${Number(row.tasks_in_review)} en revisión`);
    }
    if (Number(row?.high_open_risks || 0) > 0) {
        pieces.push(`${Number(row.high_open_risks)} riesgo(s) alto(s)`);
    }
    if (Number(row?.open_deliverables || 0) > 0) {
        pieces.push(`${Number(row.open_deliverables)} entregable(s) abierto(s)`);
    }

    return pieces.length ? pieces.join(" · ") : "Sin presión crítica visible";
}

function getViewModeMessage(viewMode, projectsCount) {
    const base = `${projectsCount} proyecto(s) en la vista actual.`;
    const map = {
        all: `${base} Lectura completa del portafolio autorizado. Prioriza proyectos en rojo, con presión operativa o alto valor.`,
        as_pm: `${base} Estás viendo los proyectos que gobiernas como PM. Prioriza bloqueos, vencimientos, riesgos abiertos y decisiones pendientes.`,
        as_sponsor: `${base} Estás viendo proyectos donde tienes patrocinio o respaldo institucional. Prioriza salud, valor, riesgo y decisiones ejecutivas.`,
        as_responsible: `${base} Estás viendo proyectos donde tienes ejecución directa. Prioriza tareas vencidas, evidencias pendientes y avances por documentar.`,
        as_reviewer: `${base} Estás viendo proyectos donde debes validar. Prioriza avances en revisión, evidencia pendiente y dictámenes atrasados.`,
        participating: `${base} Estás viendo proyectos donde participas desde algún rol PMO. Revisa qué rol tienes y qué presión requiere atención.`,
    };

    return map[viewMode] || map.all;
}


function buildProjectNarrative(row) {
    if (row?.scope_statement) return row.scope_statement;
    if (row?.strategic_impact) return row.strategic_impact;
    if (row?.description) return row.description;
    if (row?.notes) return row.notes;
    return "Proyecto PMO sin objetivo ampliado capturado. Completa alcance, impacto estratégico o notas en la Ficha 360.";
}

function truncateText(value = "", max = 240) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (text.length <= max) return text;
    return `${text.slice(0, max).trim()}…`;
}

function getPortfolioReading({ totalProjects, activeProjects, criticalProjects, atRiskProjects, projects }) {
    const pressureProjects = (projects || []).filter((row) =>
        Number(row?.overdue_tasks || 0) > 0 ||
        Number(row?.blocked_tasks || 0) > 0 ||
        Number(row?.high_open_risks || 0) > 0
    ).length;

    if (!totalProjects) {
        return "No hay proyectos en la vista actual. Ajusta filtros o cambia tu participación PMO.";
    }

    if (criticalProjects > 0) {
        return `El portafolio muestra ${criticalProjects} proyecto(s) crítico(s) y ${pressureProjects} con presión operativa visible. Prioriza Ficha 360 de proyectos en rojo, bloqueos, vencimientos y riesgos altos.`;
    }

    if (atRiskProjects > 0) {
        return `El portafolio tiene ${atRiskProjects} proyecto(s) en atención. Revisa vencimientos, evidencia pendiente y presión PMO antes de abrir nuevas cargas.`;
    }

    return `El portafolio se mantiene en control. Continúa monitoreando avance, evidencia, entregables y riesgos para sostener trazabilidad ejecutiva.`;
}

function getTopPressureProject(projects = []) {
    const rows = [...projects];

    rows.sort((a, b) => {
        const scoreA =
            Number(a?.priority_score || 0) +
            Number(a?.overdue_tasks || 0) * 8 +
            Number(a?.blocked_tasks || 0) * 12 +
            Number(a?.high_open_risks || 0) * 6;
        const scoreB =
            Number(b?.priority_score || 0) +
            Number(b?.overdue_tasks || 0) * 8 +
            Number(b?.blocked_tasks || 0) * 12 +
            Number(b?.high_open_risks || 0) * 6;

        return scoreB - scoreA;
    });

    return rows[0] || null;
}


export default function ProjectsPage() {
    const navigate = useNavigate();

    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [hoveredProjectId, setHoveredProjectId] = useState("");
    const [projects, setProjects] = useState([]);
    const [dashboard, setDashboard] = useState({
        summary: {},
        alerts: [],
        projects: [],
    });

    const [filters, setFilters] = useState(defaultFilters);
    const [viewMode, setViewMode] = useState(defaultViewMode);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [sortBy, setSortBy] = useState("priority_score");
    const [sortDir, setSortDir] = useState("desc");

    const loggedUser = useMemo(() => getLoggedUserSignals(), []);
    const loggedUserId = Number(loggedUser.userId || 0);
    const [serverLoggedUser, setServerLoggedUser] = useState(null);
    const activeLoggedUser = useMemo(() => {
        if (serverLoggedUser?.id) {
            return {
                ...loggedUser,
                userId: serverLoggedUser.id,
                displayName: serverLoggedUser.display_name || loggedUser.displayName,
                email: serverLoggedUser.email || loggedUser.email,
                role: serverLoggedUser.role || loggedUser.role,
                position: serverLoggedUser.position || serverLoggedUser.job_title || loggedUser.position,
                avatarUrl: serverLoggedUser.avatar_path || loggedUser.avatarUrl,
            };
        }

        return loggedUser;
    }, [serverLoggedUser, loggedUser]);

    const buildQueryString = (obj) => {
        const params = new URLSearchParams();
        Object.entries(obj).forEach(([key, value]) => {
            if (value !== null && value !== undefined && String(value).trim() !== "") {
                params.append(key, String(value).trim());
            }
        });
        return params.toString();
    };

    const fetchJsonSafe = async (url) => {
        const res = await fetch(url);
        const raw = await res.text();

        let data = null;
        try {
            data = raw ? JSON.parse(raw) : null;
        } catch {
            throw new Error(`Respuesta inválida en ${url}: ${raw || "[vacío]"}`);
        }

        return { res, data };
    };

    const normalizeProjectsResponse = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (payload && Array.isArray(payload.projects)) return payload.projects;
        if (payload && Array.isArray(payload.data)) return payload.data;
        return [];
    };

    const buildRequestParams = (customFilters = filters, customViewMode = viewMode) => {
        return {
            ...customFilters,
            view_mode: customViewMode,
            logged_user_id: loggedUserId || "",
        };
    };

    const fetchData = async (customFilters = filters, customViewMode = viewMode) => {
        try {
            setError("");

            const requestParams = buildRequestParams(customFilters, customViewMode);
            const query = buildQueryString(requestParams);

            const { data: projectsPayload } = await fetchJsonSafe(
                `${API_BASE}/projects_list.php${query ? `?${query}` : ""}`
            );

            const { data: dashboardPayload } = await fetchJsonSafe(
                `${API_BASE}/pmo_dashboard.php${query ? `?${query}` : ""}`
            );

            const normalizedProjects = normalizeProjectsResponse(projectsPayload);
            setServerLoggedUser(projectsPayload?.logged_user || null);

            if (!dashboardPayload?.success) {
                throw new Error(dashboardPayload?.message || "No se pudo cargar el dashboard PMO");
            }

            setProjects(normalizedProjects);
            setDashboard({
                summary: dashboardPayload.summary || {},
                alerts: Array.isArray(dashboardPayload.alerts) ? dashboardPayload.alerts : [],
                projects: Array.isArray(dashboardPayload.projects) ? dashboardPayload.projects : [],
            });
        } catch (err) {
            console.error(err);
            setError(err.message || "Error al cargar proyectos");
            setProjects([]);
            setServerLoggedUser(null);
            setDashboard({
                summary: {},
                alerts: [],
                projects: [],
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData(defaultFilters, defaultViewMode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenDuplicate = (project) => {
        setSelectedProject(project);
        setDuplicateOpen(true);
    };

    const handleDeleteProject = async (project, e) => {
        if (e) e.stopPropagation();
        const ok = window.confirm(`¿Eliminar proyecto "${project?.name || "sin nombre"}"?`);
        if (!ok) return;

        try {
            const { res, data } = await fetchJsonSafe(`${API_BASE}/project_delete.php?id=${project.id}`, {
                method: "DELETE",
            });

            if (!res.ok || !data?.success) {
                throw new Error(data?.message || data?.detail || "No se pudo eliminar el proyecto");
            }

            await fetchData(filters, viewMode);
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo eliminar el proyecto");
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        await fetchData(filters, viewMode);
    };

    const applyFilterChange = async (key, value) => {
        const nextFilters = { ...filters, [key]: value };
        setFilters(nextFilters);
        setLoading(true);
        await fetchData(nextFilters, viewMode);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchData(filters, viewMode);
    };

    const resetFilters = async () => {
        setFilters(defaultFilters);
        setViewMode(defaultViewMode);
        setLoading(true);
        await fetchData(defaultFilters, defaultViewMode);
    };

    const handleViewModeChange = async (mode) => {
        setViewMode(mode);
        setLoading(true);
        await fetchData(filters, mode);
    };

    const handleQuickKpiFilter = async (patch = {}, mode = viewMode) => {
        const nextFilters = { ...defaultFilters, ...patch };
        setFilters(nextFilters);
        setViewMode(mode);
        setLoading(true);
        await fetchData(nextFilters, mode);
    };

    const summary = dashboard.summary || {};

    const portfolioProgressAvg = useMemo(() => {
        const v = Number(summary.portfolio_progress_avg || 0);
        return Number.isFinite(v) ? v.toFixed(2) : "0.00";
    }, [summary]);

    const totalProjects = Number(summary.total_projects || projects.length || 0);
    const activeProjects = Number(summary.active_projects || 0);
    const completedProjects = Number(summary.completed_projects || 0);
    const onTrackProjects = Number(summary.on_track_projects || 0);
    const atRiskProjects = Number(summary.at_risk_projects || 0);
    const criticalProjects = Number(summary.critical_projects || 0);

    const executiveProjects = useMemo(() => {
        if (Array.isArray(dashboard.projects) && dashboard.projects.length > 0) {
            return dashboard.projects;
        }
        return projects;
    }, [dashboard.projects, projects]);

    const topPressureProject = useMemo(() => getTopPressureProject(projects), [projects]);
    const portfolioReading = useMemo(
        () => getPortfolioReading({ totalProjects, activeProjects, criticalProjects, atRiskProjects, projects }),
        [totalProjects, activeProjects, criticalProjects, atRiskProjects, projects]
    );

    const currentViewLabel = useMemo(() => {
        const found = viewModeOptions.find((v) => v.value === viewMode);
        return found ? found.label : "Todos";
    }, [viewMode]);
    const sortedProjects = useMemo(() => {
        const list = [...projects];
        const dir = sortDir === "asc" ? 1 : -1;
        const healthRank = { verde: 1, amarillo: 2, rojo: 3 };
        const statusRank = { activo: 1, pausado: 2, terminado: 3, cancelado: 4 };
        list.sort((a, b) => {
            let av;
            let bv;
            switch (sortBy) {
                case "code":
                    av = String(a.code || "").toLowerCase();
                    bv = String(b.code || "").toLowerCase();
                    break;
                case "name":
                    av = String(a.name || "").toLowerCase();
                    bv = String(b.name || "").toLowerCase();
                    break;
                case "organization":
                    av = String(a.account_name || "").toLowerCase();
                    bv = String(b.account_name || "").toLowerCase();
                    break;
                case "project_manager":
                    av = String(a.project_manager_name || a.project_manager || "").toLowerCase();
                    bv = String(b.project_manager_name || b.project_manager || "").toLowerCase();
                    break;
                case "sponsor":
                    av = String(a.sponsor_name || a.sponsor || "").toLowerCase();
                    bv = String(b.sponsor_name || b.sponsor || "").toLowerCase();
                    break;
                case "progress_percent":
                    av = Number(a.progress_percent || 0);
                    bv = Number(b.progress_percent || 0);
                    break;
                case "value":
                    av = Number(getProjectValue(a) || 0);
                    bv = Number(getProjectValue(b) || 0);
                    break;
                case "health_status":
                    av = healthRank[a.health_status] || 99;
                    bv = healthRank[b.health_status] || 99;
                    break;
                case "status":
                    av = statusRank[a.status] || 99;
                    bv = statusRank[b.status] || 99;
                    break;
                case "priority_score":
                default:
                    av = Number(a.priority_score || 0);
                    bv = Number(b.priority_score || 0);
                    break;
            }
            if (typeof av === "string" && typeof bv === "string") {
                return av.localeCompare(bv) * dir;
            }
            return ((av > bv) - (av < bv)) * dir;
        });
        return list;
    }, [projects, sortBy, sortDir]);

    if (loading) {
        return (
            <MainLayout>
                <div style={{ padding: 24, fontSize: 16 }}>Cargando módulo PMO...</div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div style={{ padding: 24, background: "linear-gradient(180deg, #001827 0%, #002b45 42%, #001827 100%)", minHeight: "100vh", color: "#e0f2fe" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 18,
                        flexWrap: "wrap",
                        marginBottom: 20,
                        padding: 24,
                        borderRadius: 26,
                        border: "1px solid rgba(0,178,255,.28)",
                        background: "linear-gradient(135deg, rgba(0,24,39,.98), rgba(0,66,103,.92))",
                        color: "#ffffff",
                        boxShadow: "0 20px 45px rgba(30,64,175,.18)",
                    }}
                >
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", color: "#bfdbfe", marginBottom: 8 }}>
                            Portafolio PMO
                        </div>
                        <h1 style={{ margin: 0, fontSize: 32, color: "#ffffff", letterSpacing: "-.04em" }}>
                            PMO · Proyectos
                        </h1>
                        <div style={{ color: "#dbeafe", marginTop: 8, fontSize: 15, maxWidth: 760, lineHeight: 1.55 }}>
                            Portafolio ejecutivo de proyectos, salud, valor, rol del usuario y presión operativa.
                        </div>
                        <div style={{ color: "#ffffff", marginTop: 12, fontSize: 13, fontWeight: 800 }}>
                            Vista actual: {currentViewLabel}
                            {activeLoggedUser.displayName ? ` · ${activeLoggedUser.displayName}` : ""}
                        </div>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 10,
                            flexWrap: "wrap",
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            title="Actualizar información del portafolio PMO."
                            style={{
                                height: 38,
                                padding: "0 18px",
                                borderRadius: 999,
                                border: "1px solid rgba(0,178,255,.42)",
                                background: "rgba(2,25,48,.72)",
                                color: "#ffffff",
                                fontSize: 12,
                                fontWeight: 900,
                                cursor: refreshing ? "not-allowed" : "pointer",
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
                            }}
                        >
                            {refreshing ? "Actualizando..." : "Actualizar"}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/project-templates")}
                            title="Abrir biblioteca de plantillas PMO."
                            style={{
                                height: 38,
                                padding: "0 18px",
                                borderRadius: 999,
                                border: "1px solid rgba(0,178,255,.42)",
                                background: "rgba(2,25,48,.72)",
                                color: "#ffffff",
                                fontSize: 12,
                                fontWeight: 900,
                                cursor: "pointer",
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
                            }}
                        >
                            Plantillas
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/projects/new")}
                            title="Crear nuevo proyecto PMO."
                            style={{
                                height: 38,
                                padding: "0 20px",
                                borderRadius: 999,
                                border: "1px solid #facc15",
                                background: "#facc15",
                                color: "#0f172a",
                                fontSize: 12,
                                fontWeight: 950,
                                cursor: "pointer",
                                boxShadow: "0 10px 24px rgba(250,204,21,.18)",
                            }}
                        >
                            + Nuevo proyecto
                        </button>
                    </div>
                </div>

                {error ? (
                    <div
                        style={{
                            background: "#fee2e2",
                            color: "#991b1b",
                            padding: 14,
                            borderRadius: 10,
                            marginBottom: 16,
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                <SectionCard
                    title="Mi participación PMO"
                    right={<Badge bg="rgba(14,165,233,.14)" color="#bfdbfe" tooltip="Vista actual del portafolio según tu participación PMO.">{currentViewLabel}</Badge>}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 18,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 240 }}>
                            <UserAvatar user={activeLoggedUser} />
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 900, color: "#ffffff" }}>
                                    {activeLoggedUser.displayName || "Usuario PMO"}
                                </div>
                                <div style={{ fontSize: 12, color: "#93c5fd", fontWeight: 700 }}>
                                    {activeLoggedUser.email || "Sesión activa"}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" }}>
                            {viewModeOptions.map((option) => {
                                const active = viewMode === option.value;

                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => handleViewModeChange(option.value)}
                                        title={option.helper}
                                        style={{
                                            padding: "11px 14px",
                                            borderRadius: 999,
                                            border: active ? "1px solid rgba(125,211,252,.9)" : "1px solid rgba(125,211,252,.28)",
                                            background: active ? "linear-gradient(135deg, #38bdf8, #8b5cf6)" : "rgba(2,25,48,.72)",
                                            color: active ? "#ffffff" : "#bfdbfe",
                                            fontWeight: 800,
                                            cursor: "pointer",
                                            boxShadow: active ? "0 10px 22px rgba(15,23,42,.12)" : "none",
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: 14,
                            padding: 14,
                            borderRadius: 14,
                            border: "1px solid rgba(125,211,252,.28)",
                            background: "rgba(14,165,233,.10)",
                            color: "#bfdbfe",
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: 1.45,
                        }}
                    >
                        {getViewModeMessage(viewMode, projects.length)}
                    </div>
                </SectionCard>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1.5fr) minmax(260px, .8fr)",
                        gap: 18,
                        marginBottom: 18,
                    }}
                >
                    <div
                        style={{
                            borderRadius: 24,
                            padding: 22,
                            background: "rgba(2,25,48,.88)",
                            border: "1px solid #e0e7ff",
                            boxShadow: "0 16px 38px rgba(15,23,42,.06)",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <span style={{ width: 34, height: 34, borderRadius: 12, display: "grid", placeItems: "center", background: "rgba(14,165,233,.10)", color: "#1d4ed8", fontWeight: 950 }}>⌁</span>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 950, color: "#ffffff" }}>Lectura ejecutiva del portafolio</div>
                                <div style={{ fontSize: 12, color: "#93c5fd", fontWeight: 700 }}>Qué debe mirar primero el PM antes de abrir una Ficha 360.</div>
                            </div>
                        </div>
                        <div style={{ color: "#dbeafe", fontSize: 14, lineHeight: 1.7, fontWeight: 650 }}>
                            {portfolioReading}
                        </div>
                    </div>

                    <div
                        style={{
                            borderRadius: 24,
                            padding: 22,
                            background: "rgba(67,20,7,.55)",
                            border: "1px solid rgba(251,146,60,.45)",
                            boxShadow: "0 16px 38px rgba(15,23,42,.04)",
                        }}
                    >
                        <div style={{ fontSize: 12, color: "#fed7aa", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>
                            Abrir primero
                        </div>
                        <div style={{ color: "#ffffff", fontWeight: 950, lineHeight: 1.35 }}>
                            {topPressureProject?.name || "Sin proyecto prioritario"}
                        </div>
                        <div style={{ color: "#fed7aa", fontSize: 12, fontWeight: 800, marginTop: 8 }}>
                            {topPressureProject ? buildPmoPressureText(topPressureProject) : "No hay presión visible con los filtros actuales."}
                        </div>
                    </div>
                </div>

                {/*<SectionCard title="Diagnóstico rápido">*/}
                {/*    <div style={{ fontSize: 14, color: "#dbeafe" }}>*/}
                {/*        <div><strong>Usuario logueado:</strong> {activeLoggedUser.displayName || activeLoggedUser.email || "—"}</div>*/}
                {/*        <div><strong>ID usuario:</strong> {loggedUserId || "—"}</div>*/}
                {/*        <div><strong>Modo vista:</strong> {currentViewLabel}</div>*/}
                {/*        <div><strong>Proyectos cargados:</strong> {projects.length}</div>*/}
                {/*        <div><strong>Proyectos dashboard:</strong> {dashboard.projects?.length || 0}</div>*/}
                {/*        <div><strong>Alertas:</strong> {dashboard.alerts?.length || 0}</div>*/}
                {/*    </div>*/}
                {/*</SectionCard>*/}

                <SectionCard title="KPIs del portafolio · filtros rápidos">
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 16,
                        }}
                    >
                        <StatCard title="Total de proyectos" value={totalProjects} subtitle="Portafolio filtrado" onClick={() => handleQuickKpiFilter({})} />
                        <StatCard title="Proyectos activos" value={activeProjects} subtitle="En operación actual" bg="#f8fafc" onClick={() => handleQuickKpiFilter({ status: "activo" })} />
                        <StatCard title="Terminados" value={completedProjects} subtitle="Cerrados correctamente" bg="#eff6ff" onClick={() => handleQuickKpiFilter({ status: "terminado" })} />
                        <StatCard title="En control" value={onTrackProjects} subtitle="Proyectos sanos" bg="#ecfdf5" onClick={() => handleQuickKpiFilter({ health_status: "verde" })} />
                        <StatCard title="En atención" value={atRiskProjects} subtitle="Con señales de alerta" bg="#fffbeb" onClick={() => handleQuickKpiFilter({ health_status: "amarillo" })} />
                        <StatCard title="Críticos" value={criticalProjects} subtitle="Riesgo ejecutivo alto" bg="#fef2f2" onClick={() => handleQuickKpiFilter({ health_status: "rojo" })} />
                        <StatCard
                            title="Presupuesto planeado"
                            value={Number(summary.total_budget_planned || 0).toLocaleString("es-MX")}
                            subtitle="Suma del portafolio"
                            bg="#eef2ff"
                        />
                        <StatCard
                            title="Presupuesto actual"
                            value={Number(summary.total_budget_actual || 0).toLocaleString("es-MX")}
                            subtitle="Ejercido / actualizado"
                            bg="#ecfeff"
                        />
                        <StatCard
                            title="Valor contractual"
                            value={Number(summary.total_contract_value || 0).toLocaleString("es-MX")}
                            subtitle="Valor total de contratos"
                            bg="#f5f3ff"
                        />
                    </div>

                    <div
                        style={{
                            marginTop: 18,
                            background: "rgba(2,25,48,.72)",
                            border: "1px solid rgba(0,178,255,.25)",
                            borderRadius: 18,
                            padding: 16,
                        }}
                    >
                        <div style={{ fontSize: 13, color: "#93c5fd", marginBottom: 8 }}>
                            Avance promedio del portafolio
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#ffffff", marginBottom: 10 }}>
                            {portfolioProgressAvg}%
                        </div>
                        <div
                            style={{
                                width: "100%",
                                height: 12,
                                background: "rgba(148,163,184,.25)",
                                borderRadius: 999,
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    width: `${Math.max(0, Math.min(100, Number(portfolioProgressAvg)))}%`,
                                    height: "100%",
                                    background: "#2563eb",
                                }}
                            />
                        </div>
                    </div>
                </SectionCard>

                {/*<SectionCard*/}
                {/*    title="PMO · Alertas ejecutivas"*/}
                {/*    right={<Badge bg="#f3f4f6">{dashboard.alerts?.length || 0} alertas</Badge>}*/}
                {/*>*/}
                {/*    {!dashboard.alerts || dashboard.alerts.length === 0 ? (*/}
                {/*        <div style={{ color: "#93c5fd" }}>*/}
                {/*            No hay alertas ejecutivas activas con los filtros actuales.*/}
                {/*        </div>*/}
                {/*    ) : (*/}
                {/*        <div style={{ display: "grid", gap: 12 }}>*/}
                {/*            {dashboard.alerts.map((alert, idx) => (*/}
                {/*                <div*/}
                {/*                    key={alert.id || `${alert.project_id || "alert"}-${idx}`}*/}
                {/*                    style={{*/}
                {/*                        border: "1px solid #e5e7eb",*/}
                {/*                        borderRadius: 12,*/}
                {/*                        padding: 14,*/}
                {/*                        background: "#fff",*/}
                {/*                    }}*/}
                {/*                >*/}
                {/*                    <div*/}
                {/*                        style={{*/}
                {/*                            display: "flex",*/}
                {/*                            justifyContent: "space-between",*/}
                {/*                            gap: 10,*/}
                {/*                            flexWrap: "wrap",*/}
                {/*                            marginBottom: 8,*/}
                {/*                        }}*/}
                {/*                    >*/}
                {/*                        <div style={{ fontWeight: 700, color: "#ffffff" }}>*/}
                {/*                            {alert.project_name || "Proyecto"}*/}
                {/*                        </div>*/}
                {/*                        <div>*/}
                {/*                            <HealthBadge health={alert.health_status} />*/}
                {/*                            {alert.priority_score !== undefined ? (*/}
                {/*                                <PriorityScoreBadge score={alert.priority_score} />*/}
                {/*                            ) : null}*/}
                {/*                            {alert.alert_type ? <Badge bg="#fee2e2">{alert.alert_type}</Badge> : null}*/}
                {/*                        </div>*/}
                {/*                    </div>*/}

                {/*                    <div style={{ color: "#dbeafe", fontSize: 14, marginBottom: 10 }}>*/}
                {/*                        {alert.message || "Alerta ejecutiva detectada en el portafolio PMO."}*/}
                {/*                    </div>*/}

                {/*                    <ActionButton*/}
                {/*                        onClick={() => navigate(`/projects/${alert.project_id || alert.id}`)}*/}
                {/*                        bg="#fff"*/}
                {/*                        color="#111827"*/}
                {/*                        border="1px solid #d1d5db"*/}
                {/*                    >*/}
                {/*                        Ver proyecto*/}
                {/*                    </ActionButton>*/}
                {/*                </div>*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    )}*/}
                {/*</SectionCard>*/}
                <SectionCard
                    title="Criterio de uso PMO"
                    right={<Badge bg="rgba(14,165,233,.14)" color="#bfdbfe" tooltip="Cantidad de proyectos en la vista actual.">{projects.length} proyectos</Badge>}
                >
                    <div style={{ color: "#dbeafe", fontSize: 14, lineHeight: 1.65 }}>
                        {getViewModeMessage(viewMode, projects.length)}
                        {" "}
                        Esta pantalla sirve como inventario ejecutivo del portafolio. El Dashboard mide salud; Centro de Decisiones prioriza acciones; Proyectos ayuda a elegir qué Ficha 360 abrir.
                    </div>
                </SectionCard>

                <SectionCard title="Filtros PMO">
                    <form onSubmit={handleSearch}>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "2fr repeat(5, minmax(150px, 1fr))",
                                gap: 14,
                                alignItems: "end",
                            }}
                        >
                            <Input
                                label="Buscar"
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                                }
                                placeholder="Proyecto, organización, sponsor o PM..."
                            />

                            <Select
                                label="Etapa"
                                value={filters.stage}
                                onChange={(e) => applyFilterChange("stage", e.target.value)}
                            >
                                {stageOptions.map((opt) => (
                                    <option key={opt.value || "all-stage"} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Select>

                            <Select
                                label="Estatus"
                                value={filters.status}
                                onChange={(e) => applyFilterChange("status", e.target.value)}
                            >
                                {statusOptions.map((opt) => (
                                    <option key={opt.value || "all-status"} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Select>

                            <Select
                                label="Salud"
                                value={filters.health_status}
                                onChange={(e) => applyFilterChange("health_status", e.target.value)}
                            >
                                {healthOptions.map((opt) => (
                                    <option key={opt.value || "all-health"} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Select>

                            <Input
                                label="Tipo de proyecto"
                                value={filters.project_type}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, project_type: e.target.value }))
                                }
                                placeholder="tecnología / estratégico / gobierno"
                            />

                            <Select
                                label="Prioridad"
                                value={filters.priority}
                                onChange={(e) => applyFilterChange("priority", e.target.value)}
                            >
                                {priorityOptions.map((opt) => (
                                    <option key={opt.value || "all-priority"} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div style={{ marginTop: 10, color: "#93c5fd", fontSize: 12, fontWeight: 700 }}>
                            Los filtros se aplican al cambiar combos o presionar Enter en búsqueda.
                            <button
                                type="button"
                                onClick={resetFilters}
                                style={{
                                    marginLeft: 10,
                                    border: 0,
                                    background: "transparent",
                                    color: "#facc15",
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                }}
                            >
                                Limpiar
                            </button>
                        </div>
                    </form>
                </SectionCard>



                {/*<SectionCard*/}
                {/*    title="Radar ejecutivo por proyecto"*/}
                {/*    right={<Badge bg="#f3f4f6">{executiveProjects.length} proyectos</Badge>}*/}
                {/*>*/}
                {/*    {executiveProjects.length === 0 ? (*/}
                {/*        <div style={{ color: "#93c5fd" }}>No hay proyectos para mostrar.</div>*/}
                {/*    ) : (*/}
                {/*        <div*/}
                {/*            style={{*/}
                {/*                display: "grid",*/}
                {/*                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",*/}
                {/*                gap: 16,*/}
                {/*            }}*/}
                {/*        >*/}
                {/*            {executiveProjects.map((row) => (*/}
                {/*                <div*/}
                {/*                    key={row.id}*/}
                {/*                    style={{*/}
                {/*                        background: "#fff",*/}
                {/*                        border: "1px solid #e5e7eb",*/}
                {/*                        borderRadius: 14,*/}
                {/*                        padding: 16,*/}
                {/*                        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",*/}
                {/*                    }}*/}
                {/*                >*/}
                {/*                    <div*/}
                {/*                        style={{*/}
                {/*                            display: "flex",*/}
                {/*                            justifyContent: "space-between",*/}
                {/*                            alignItems: "flex-start",*/}
                {/*                            gap: 10,*/}
                {/*                            flexWrap: "wrap",*/}
                {/*                            marginBottom: 10,*/}
                {/*                        }}*/}
                {/*                    >*/}
                {/*                        <div>*/}
                {/*                            <div style={{ fontSize: 17, fontWeight: 700, color: "#ffffff" }}>*/}
                {/*                                {row.name || "Proyecto sin nombre"}*/}
                {/*                            </div>*/}
                {/*                            <div style={{ color: "#93c5fd", fontSize: 13, marginTop: 4 }}>*/}
                {/*                                {row.account_name || "Sin cuenta"} · {row.project_manager_name || row.project_manager || "Sin PM"}*/}
                {/*                            </div>*/}
                {/*                        </div>*/}

                {/*                        <HealthBadge health={row.health_status} />*/}
                {/*                    </div>*/}

                {/*                    <div style={{ marginBottom: 10 }}>*/}
                {/*                        <Badge bg={getStageBg(row.stage)}>Etapa: {row.stage || "—"}</Badge>*/}
                {/*                        <Badge bg={getStatusBg(row.status)}>Estatus: {row.status || "—"}</Badge>*/}
                {/*                        <Badge bg="#ede9fe">Tipo: {row.project_type || "—"}</Badge>*/}
                {/*                        <Badge bg="#e0f2fe">Prioridad: {row.priority || "—"}</Badge>*/}
                {/*                    </div>*/}

                {/*                    <div style={{ marginBottom: 10 }}>*/}
                {/*                        <PriorityScoreBadge score={row.priority_score} />*/}
                {/*                    </div>*/}

                {/*                    <div style={{ marginBottom: 14 }}>*/}
                {/*                        <div style={{ fontSize: 12, color: "#93c5fd", marginBottom: 6 }}>*/}
                {/*                            Avance del proyecto*/}
                {/*                        </div>*/}
                {/*                        <ProgressBar value={row.progress_percent || 0} />*/}
                {/*                    </div>*/}

                {/*                    <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Código:</strong> {row.code || "—"}*/}
                {/*                        </div>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Sponsor:</strong> {row.sponsor_name || row.sponsor || "—"}*/}
                {/*                        </div>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Oportunidad:</strong> {row.opportunity_name || "—"}*/}
                {/*                        </div>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Fin planeado:</strong> {row.planned_end_date || row.end_date || "—"}*/}
                {/*                        </div>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Tareas vencidas:</strong> {row.overdue_tasks || 0}*/}
                {/*                        </div>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Riesgos altos abiertos:</strong> {row.high_open_risks || 0}*/}
                {/*                        </div>*/}
                {/*                        <div style={{ fontSize: 13, color: "#dbeafe" }}>*/}
                {/*                            <strong>Tareas bloqueadas:</strong> {row.blocked_tasks || 0}*/}
                {/*                        </div>*/}
                {/*                    </div>*/}

                {/*                    <ActionButton onClick={() => navigate(`/projects/${row.id}`)}>*/}
                {/*                        Ver ficha 360*/}
                {/*                    </ActionButton>*/}
                {/*                </div>*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    )}*/}
                {/*</SectionCard>*/}

                <SectionCard
                    title="Portafolio PMO de proyectos"
                    right={(
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Badge bg="rgba(14,165,233,.14)" color="#bfdbfe" tooltip="Proyectos visibles con los filtros actuales.">{projects.length} registros</Badge>
                            <Badge bg="rgba(250,204,21,.16)" color="#fde68a" tooltip="Cada proyecto se muestra como renglón amplio tipo ORBIS.">Vista amplia</Badge>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                style={{
                                    padding: "8px 10px",
                                    borderRadius: 999,
                                    border: "1px solid rgba(125,211,252,.35)",
                                    background: "rgba(2,25,48,.88)",
                                    color: "#e0f2fe",
                                    fontSize: 12,
                                    fontWeight: 800,
                                }}
                                title="Ordenar proyectos"
                            >
                                <option value="priority_score">Ordenar: Score</option>
                                <option value="progress_percent">Ordenar: Avance</option>
                                <option value="value">Ordenar: Valor</option>
                                <option value="health_status">Ordenar: Salud</option>
                                <option value="status">Ordenar: Estatus</option>
                                <option value="code">Ordenar: Código</option>
                                <option value="name">Ordenar: Proyecto</option>
                                <option value="organization">Ordenar: Organización</option>
                                <option value="project_manager">Ordenar: PM</option>
                                <option value="sponsor">Ordenar: Sponsor</option>
                            </select>
                            <button
                                type="button"
                                onClick={() => setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))}
                                style={{
                                    padding: "8px 12px",
                                    borderRadius: 999,
                                    border: "1px solid rgba(125,211,252,.35)",
                                    background: "rgba(2,25,48,.88)",
                                    color: "#e0f2fe",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                }}
                                title="Cambiar dirección de orden"
                            >
                                {sortDir === "asc" ? "Asc ↑" : "Desc ↓"}
                            </button>
                        </div>
                    )}
                >
                    <div style={{ color: "#93c5fd", fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Ordena por score, avance, valor o responsables para leer el portafolio con criterio ejecutivo.</div>
                    {projects.length === 0 ? (
                        <div style={{ color: "#93c5fd" }}>No hay proyectos registrados.</div>
                    ) : (
                        <div style={{ display: "grid", gap: 14 }}>
                            {sortedProjects.map((row, index) => {
                                const isHovered = String(hoveredProjectId || "") === String(row.id || "");
                                const codeColor = "#facc15";
                                const pressure = buildPmoPressureText(row);
                                const hasPressure = pressure !== "Sin presión crítica visible";

                                return (
                                    <div
                                        key={row.id}
                                        onMouseEnter={() => setHoveredProjectId(String(row.id || ""))}
                                        onMouseLeave={() => setHoveredProjectId("")}
                                        onClick={() => navigate(`/projects/${row.id}`)}
                                        style={{
                                            borderRadius: 18,
                                            border: isHovered ? "1px solid rgba(56,189,248,.75)" : "1px solid rgba(0,178,255,.26)",
                                            background: isHovered
                                                ? "linear-gradient(180deg, rgba(8,47,73,.98), rgba(2,38,63,.98))"
                                                : index % 2 === 0
                                                    ? "rgba(3,37,60,.92)"
                                                    : "rgba(2,31,51,.92)",
                                            boxShadow: isHovered ? "0 18px 38px rgba(14,165,233,.12)" : "0 12px 28px rgba(0,0,0,.16)",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            transition: "all .18s ease",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "minmax(260px, 1.3fr) repeat(7, minmax(120px, .7fr)) minmax(135px, .5fr)",
                                                gap: 16,
                                                alignItems: "start",
                                                padding: "18px 18px 14px",
                                            }}
                                        >
                                            <div>
                                                <div style={{ color: codeColor, fontSize: 12, fontWeight: 950, letterSpacing: ".06em", marginBottom: 6 }}>
                                                    {row.code || "SIN-CÓDIGO"}
                                                </div>
                                                <div style={{ color: "#ffffff", fontSize: 14, fontWeight: 850, lineHeight: 1.32 }}>
                                                    {row.name || "—"}
                                                </div>
                                                <div style={{ color: "#7dd3fc", fontSize: 11, marginTop: 5, fontWeight: 700 }}>
                                                    {row.project_type || "—"} · {row.priority || "—"}
                                                </div>
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="Organización" tooltip="Organización vinculada al proyecto. Puede venir del CRM o ser un proyecto independiente." />
                                                <div style={{ color: "#e0f2fe", fontWeight: 700, fontSize: 13, lineHeight: 1.35 }}>{row.account_name || "Proyecto independiente"}</div>
                                                <div style={{ color: "#93c5fd", fontSize: 11, marginTop: 4 }}>
                                                    {row.account_id && row.account_name ? "Vinculación opcional CRM" : "Sin dependencia CRM"}
                                                </div>
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="PM" tooltip="Project Manager responsable de gobernar avance, bloqueos, decisiones y trazabilidad PMO." />
                                                <div style={{ color: "#dbeafe", fontWeight: 650, fontSize: 13, lineHeight: 1.35 }}>{row.project_manager_name || row.project_manager || "—"}</div>
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="Sponsor" tooltip="Patrocinador o autoridad que respalda decisiones estratégicas del proyecto." />
                                                <div style={{ color: "#dbeafe", fontWeight: 650, fontSize: 13, lineHeight: 1.35 }}>{row.sponsor_name || row.sponsor || "—"}</div>
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="Mi rol" tooltip="Participación del usuario activo en este proyecto: PM, sponsor, responsable o revisor." />
                                                <Badge
                                                    bg={row.my_project_role && row.my_project_role !== "—" ? "rgba(139,92,246,.18)" : "rgba(148,163,184,.12)"}
                                                    color={row.my_project_role && row.my_project_role !== "—" ? "#ddd6fe" : "#cbd5e1"}
                                                    tooltip="Rol del usuario activo dentro de este proyecto."
                                                >
                                                    {row.my_project_role || "—"}
                                                </Badge>
                                                {Number(row.my_owner_tasks || 0) > 0 ? (
                                                    <div style={{ color: "#93c5fd", fontSize: 11 }}>
                                                        {Number(row.my_owner_tasks)} tarea(s) responsable
                                                    </div>
                                                ) : null}
                                                {Number(row.my_review_tasks || 0) > 0 ? (
                                                    <div style={{ color: "#93c5fd", fontSize: 11 }}>
                                                        {Number(row.my_review_tasks)} tarea(s) revisor
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="Estado" tooltip="Etapa, estatus y salud ejecutiva del proyecto." />
                                                <Badge
                                                    bg={getStageStyle(row.stage).bg}
                                                    color={getStageStyle(row.stage).color}
                                                    border={getStageStyle(row.stage).border}
                                                    tooltip="Etapa del proyecto dentro de su ciclo de vida."
                                                >
                                                    {row.stage || "—"}
                                                </Badge>
                                                <Badge
                                                    bg={getStatusStyle(row.status).bg}
                                                    color={getStatusStyle(row.status).color}
                                                    border={getStatusStyle(row.status).border}
                                                    tooltip="Estatus operativo actual del proyecto."
                                                >
                                                    {row.status || "—"}
                                                </Badge>
                                                <HealthBadge health={row.health_status} />
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="Score / avance" tooltip="Score indica presión PMO; avance indica progreso registrado. No significan lo mismo." />
                                                <PriorityScoreBadge score={row.priority_score} />
                                                <ProgressBar value={row.progress_percent || 0} />
                                            </div>

                                            <div>
                                                <ProjectMiniLabel label="Valor" tooltip="Valor económico del proyecto, usando contrato o presupuesto planeado." />
                                                <div style={{ color: "#ffffff", fontWeight: 800, fontSize: 13, lineHeight: 1.3 }}>
                                                    {formatMoney(getProjectValue(row), row.currency || "MXN")}
                                                </div>
                                                <div style={{ color: "#93c5fd", fontSize: 11 }}>{row.currency || "MXN"}</div>
                                                <div style={{ color: "#bfdbfe", fontSize: 11, marginTop: 8 }}>
                                                    {formatDateLong(row.planned_start_date || row.start_date)} → {formatDateLong(row.planned_end_date || row.end_date)}
                                                </div>
                                            </div>

                                            <div style={{ display: "grid", gap: 8, justifyItems: "stretch" }}>
                                                <ActionButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/projects/${row.id}`);
                                                    }}
                                                    bg="#8b5cf6"
                                                    color="#ffffff"
                                                    border="1px solid rgba(167,139,250,.8)"
                                                >
                                                    Ficha 360
                                                </ActionButton>
                                                <ActionButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenDuplicate(row);
                                                    }}
                                                    bg="#facc15"
                                                    color="#111827"
                                                    border="1px solid #facc15"
                                                >
                                                    Duplicar
                                                </ActionButton>
                                                <ActionButton
                                                    onClick={(e) => handleDeleteProject(row, e)}
                                                    bg="transparent"
                                                    color="#fecaca"
                                                    border="1px solid rgba(248,113,113,.65)"
                                                >
                                                    Eliminar
                                                </ActionButton>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                borderTop: "1px solid rgba(0,178,255,.18)",
                                                padding: "14px 18px 16px",
                                                display: "grid",
                                                gridTemplateColumns: "1fr minmax(240px, .38fr)",
                                                gap: 14,
                                                alignItems: "start",
                                                background: "rgba(8,47,73,.55)",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    border: "1px solid rgba(0,178,255,.32)",
                                                    background: "rgba(14,165,233,.08)",
                                                    borderRadius: 14,
                                                    padding: 14,
                                                }}
                                            >
                                                <div style={{ color: "#facc15", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".12em", marginBottom: 7 }}>
                                                    Objetivo / lectura PMO del proyecto
                                                </div>
                                                <div style={{ color: "#dbeafe", fontSize: 13, lineHeight: 1.6, fontWeight: 700 }}>
                                                    {truncateText(buildProjectNarrative(row), 260)}
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    minHeight: 58,
                                                    padding: "12px 14px",
                                                    borderRadius: 14,
                                                    background: hasPressure ? "rgba(251,146,60,.14)" : "rgba(16,185,129,.12)",
                                                    color: hasPressure ? "#fed7aa" : "#bbf7d0",
                                                    border: hasPressure ? "1px solid rgba(251,146,60,.5)" : "1px solid rgba(16,185,129,.45)",
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    lineHeight: 1.45,
                                                    textAlign: "center",
                                                }}
                                            >
                                                {pressure}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </SectionCard>
            </div>
            <DuplicateProjectModal
                open={duplicateOpen}
                onClose={() => setDuplicateOpen(false)}
                project={selectedProject}
                onDuplicated={() => {
                    fetchData(filters, viewMode);
                }}
            />
        </MainLayout>
    );
}
